import { gl } from '../../../Engine';
import { ImageObject } from '../../../data/image/ImageObject';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';
import { UniformVec2 } from '../../utils/uniforms/UniformVec2';
import { UniformVec3 } from '../../utils/uniforms/UniformVec3';
import { Renderer } from '../Renderer';
import {
    defaultImageFragmentShader,
    defaultImageVertexShader,
    originalImageFragmentShader,
    originalImageVertexShader,
} from './glsl';

/**
 * Renderer that will render {@link ImageObject ImageObjects}.
 */
export class ImageRenderer implements Renderer {
    /**
     * The default shader that will render {@link ImageObject ImageObjects} on {@link PlaneView}
     * without deformations or other fancy stuff.
     */
    private defaultShader: DefaultImageShader;

    private originalShader: OriginalImageShader;

    public constructor() {
        this.defaultShader = new DefaultImageShader();
        this.originalShader = new OriginalImageShader();
    }

    /**
     * Method to delete the {@link ImageRenderer}.\
     * This will deallocate the shaders after which this {@link ImageRenderer} should no longer be used.
     * @returns True to indicate that the {@link ImageRenderer} was successfully deleted.
     */
    public delete(): boolean {
        this.defaultShader.delete();
        this.originalShader.delete();
        return true;
    }

    /**
     * Method to render {@link ImageObject ImageObjects}.
     * @throws if {@link view} of unsupported type is provided. Currently only {@link PlaneView PlaneViews} are supported.
     * @param view The {@link PlaneView} whose {@link PlaneView.projectionViewMatrix projectionViewMatrix} is used for projection.
     * @param images An array of {@link ImageObject ImageObjects} that are to be rendered.
     * @returns True if the {@link images} were successfully rendered, false otherwise.
     */
    public render(view: TextureView, images: ImageObject[]): boolean {
        if (view instanceof PlaneView) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.MAX);
            gl.blendFunc(gl.ONE, gl.ONE);

            for (const image of images) {
                //intersection between the planeview and image's mesh
                const vao = image.crossSection(view.projectionViewMatrix);
                if (vao) {
                    //vao is undefined if scan is out of viewport
                    image.bindTexture();

                    vao.bind(0, 1);
                    this.renderDefault(image, vao.indexCount);
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
            gl.disable(gl.BLEND);
            return true;
        } else {
            throw new Error(
                `ImageRendering on View of type ${view.constructor} currently not supported.`
            );
        }
    }

    /**
     * Method to render {@link ImageObject ImageObjects}.
     * @throws if {@link view} of unsupported type is provided. Currently only {@link PlaneView PlaneViews} are supported.
     * @param view The {@link PlaneView} whose {@link PlaneView.projectionViewMatrix projectionViewMatrix} is used for projection.
     * @param images An array of {@link ImageObject ImageObjects} that are to be rendered.
     * @returns True if the {@link images} were successfully rendered, false otherwise.
     */
    public renderOriginal(view: TextureView, images: ImageObject[]): boolean {
        if (view instanceof PlaneView) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.MAX);
            gl.blendFunc(gl.ONE, gl.ONE);

            this.originalShader.start();
            for (const image of images) {
                //intersection between the planeview and image's mesh
                const vao = image.crossSection(view.projectionViewMatrix);
                if (vao) {
                    //vao is undefined if scan is out of viewport
                    image.bindTexture();
                    this.originalShader.minValue.loadFloat(image.minValue);

                    vao.bind(0, 1);
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
            this.originalShader.stop();
            gl.disable(gl.BLEND);
            return true;
        } else {
            throw new Error(
                `ImageRendering on View of type ${view.constructor} currently not supported.`
            );
        }
    }

    /**
     * Method to perform the image rendering using the {@link DefaultImageShader}.\
     * This method {@link ShaderProgram.start binds} the {@link DefaultImageShader},\
     * Loads values to uniform variables,\
     * {@link gl.drawElements renders the triangles},\
     * and {@link ShaderProgram.stop unbinds} the {@link DefaultImageShader}.
     *
     * @param image The {@link ImageObject} that is being rendered.\
     * Used to get the values for uniform variables.
     * @param indexCount Number of indices on the intersection {@link VertexArrayObject}.
     */
    private renderDefault(image: ImageObject, indexCount: number) {
        this.defaultShader.start();
        this.defaultShader.windowLevel.loadVec2(
            image.windowLevel ? image.windowLevel.asVec2 : [0, 0]
        );
        this.defaultShader.colorMask.loadVec3(image.colorMask);
        this.defaultShader.alpha.loadFloat(image.alpha);
        gl.drawElements(gl.TRIANGLES, indexCount, gl.UNSIGNED_INT, 0);
        this.defaultShader.stop();
    }
}

/**
 * {@link ShaderProgram} that will render {@link ImageObject}-{@link PlaneView} intersections.\
 * The shader has two inputs:
 * 1. in vec2 position, the intersection vertex coordinates in destination texture space.
 * 2. in vec3 textureCoords, which are the 3D texture coordinates of the image.
 *
 * It also has 3 uniform variables:
 * 1. uniform vec2 windowLevel, which is the {@link ImageObject.windowingLevel windowingLevel} of the {@link ImageObject}.
 * 2. uniform vec3 colorMask, which is the {@link ImageObject.colorMask colorMask} of the {@link ImageObject}.
 * 3. uniform float alpha, which is the {@link ImageObject.alpha alpha} of the {@link ImageObject}.
 */
class DefaultImageShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = defaultImageVertexShader;
    private static readonly FRAGMENT_CONTENT = defaultImageFragmentShader;

    public readonly windowLevel: UniformVec2;
    public readonly colorMask: UniformVec3;
    public readonly alpha: UniformFloat;

    constructor() {
        super(
            DefaultImageShader.VERTEX_CONTENT,
            DefaultImageShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );

        this.windowLevel = new UniformVec2('windowLevel');
        this.colorMask = new UniformVec3('colorMask');
        this.alpha = new UniformFloat('alpha');

        this.storeUniformLocations(
            this.windowLevel,
            this.colorMask,
            this.alpha
        );
    }
}

class OriginalImageShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = originalImageVertexShader;
    private static readonly FRAGMENT_CONTENT = originalImageFragmentShader;

    public readonly minValue: UniformFloat;

    constructor() {
        super(
            OriginalImageShader.VERTEX_CONTENT,
            OriginalImageShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.minValue = new UniformFloat('minValue');
        this.storeUniformLocations(this.minValue);
    }
}
